if (!customElements.get('landing-page-form')) {
  const ATTENTIVE_ERRORS = {
    invalid_phone: 'Please enter a valid US phone number.',
    invalid_signup_source_id: 'Something went wrong on our end. Please try again.',
    rate_limited: 'We are receiving a lot of entries right now. Please try again in a moment.',
    rejected_by_attentive: 'We could not sign up that phone number. Please check it and try again.',
    attentive_unavailable: 'Something went wrong on our end. Please try again.',
    server_misconfigured: 'Something went wrong on our end. Please try again.',
    network: 'Connection problem. Please check your network and try again.',
  };

  class LandingPageForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form.lpf__form');
      this.success = this.querySelector('.lpf__success');
      this.error = this.querySelector('.lpf__error');
      this.toast = this.querySelector('.lpf__toast');
      this.submitBtn = this.form ? this.form.querySelector('.lpf__cta') : null;
      this.gateBtn = this.dataset.listrakGateId ? document.getElementById(this.dataset.listrakGateId) : null;
      if (this.form) {
        // The page never reloads. Order: validate -> Attentive (phone, via
        // the optin middleware) -> Listrak (email, fired through the hidden
        // gate button) -> success state.
        this.form.addEventListener('submit', (event) => {
          event.preventDefault();
          this.handleSubmit();
        });
      }

      // Rules modal (native <dialog>)
      this.querySelectorAll('[data-lpf-modal-open]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const dialog = document.getElementById(btn.dataset.lpfModalOpen);
          if (dialog) dialog.showModal();
        });
      });
      this.querySelectorAll('dialog.lpf__modal').forEach((dialog) => {
        dialog.querySelectorAll('[data-lpf-modal-close]').forEach((b) => b.addEventListener('click', () => dialog.close()));
        // A click on the backdrop targets the <dialog> element itself
        dialog.addEventListener('click', (event) => {
          if (event.target === dialog) dialog.close();
        });
      });

      // US phone mask: (123) 456-7890
      this.querySelectorAll('input[data-attentive-phone]').forEach((input) => {
        input.addEventListener('input', (e) => {
          var x = e.target.value.replace(/\D/g, '').match(/(\d{0,3})(\d{0,3})(\d{0,4})/);
          e.target.value = !x[2] ? x[1] : '(' + x[1] + ') ' + x[2] + (x[3] ? '-' + x[3] : '');
        });
      });

      // Date mask: MM/DD/YYYY
      this.querySelectorAll('input[data-date-mask]').forEach((input) => {
        input.addEventListener('input', (e) => {
          var x = e.target.value.replace(/\D/g, '').match(/(\d{0,2})(\d{0,2})(\d{0,4})/);
          e.target.value = !x[2] ? x[1] : x[1] + '/' + x[2] + (x[3] ? '/' + x[3] : '');
        });
      });
    }

    get phoneInput() {
      return this.form.querySelector('input[data-attentive-phone]');
    }

    async handleSubmit() {
      if (this.busy) return;
      // Required fields (form is novalidate: no native bubbles, a toast instead)
      const missing = this.firstMissingRequired();
      if (missing) {
        this.showError(this.requiredMessage(missing));
        missing.focus();
        return;
      }
      const emailId = this.dataset.listrakEmailId;
      const email = emailId ? document.getElementById(emailId) : this.form.querySelector('input[type="email"]');
      if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value.trim())) {
        this.showError('Please enter a valid email address');
        email.focus();
        return;
      }
      if (this.dataset.listrakSubscribeId && !(window._ltk && window._ltk.Signup)) {
        this.showError('Signup is temporarily unavailable. Please try again in a moment.');
        return;
      }

      const phone = this.phoneInput ? this.phoneInput.value.trim() : '';
      const digits = phone.replace(/\D/g, '');
      const phoneRequired = this.dataset.phoneRequired === 'true';
      if (phoneRequired && !digits) {
        this.showError(ATTENTIVE_ERRORS.invalid_phone);
        this.phoneInput.focus();
        return;
      }
      if (digits && !(digits.length === 10 || (digits.length === 11 && digits[0] === '1'))) {
        this.showError(ATTENTIVE_ERRORS.invalid_phone);
        this.phoneInput.focus();
        return;
      }

      this.setBusy(true);
      this.showError('');

      // Attentive first: a failed SMS opt-in stops the flow so the message
      // shown to the visitor is honest. Skipped when no phone was entered.
      if (digits && this.dataset.attentiveEndpoint && this.dataset.attentiveSourceId) {
        const ok = await this.subscribeAttentive(phone);
        if (!ok) {
          this.setBusy(false);
          return;
        }
      }

      // Listrak: the SDK is bound to the hidden gate button (TYPE.CLICK)
      if (this.gateBtn) this.gateBtn.click();

      this.setBusy(false);
      this.showSuccess();
    }

    async subscribeAttentive(phone) {
      // Body shape expected by the middleware:
      // { phone, signUpSourceId, attributes: { lp_target: "ProWash360_contacts_2026" } }
      const payload = { phone: phone, signUpSourceId: this.dataset.attentiveSourceId };
      // firstName / lastName: from dedicated first/last fields when the form
      // has them, otherwise the single "name" field is split on the first space.
      const name = this.collectName();
      if (name.firstName) payload.firstName = name.firstName;
      if (name.lastName) payload.lastName = name.lastName;
      const attrKey = (this.dataset.attentiveAttrKey || '').trim();
      const attrValue = (this.dataset.attentiveAttrValue || '').trim();
      if (attrKey && attrValue) payload.attributes = { [attrKey]: attrValue };
      try {
        const res = await fetch(this.dataset.attentiveEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        let body = {};
        try {
          body = await res.json();
        } catch (_) {
          body = {};
        }
        if (!res.ok || body.success === false) {
          this.showError(ATTENTIVE_ERRORS[body.error] || ATTENTIVE_ERRORS.attentive_unavailable);
          return false;
        }
        return true;
      } catch (_) {
        this.showError(ATTENTIVE_ERRORS.network);
        return false;
      }
    }

    collectName() {
      const val = (sel) => {
        const el = this.form.querySelector(sel);
        return el ? el.value.trim() : '';
      };
      let firstName = val('input[name="first_name"], input[name="firstname"], input[data-param="first_name"]');
      let lastName = val('input[name="last_name"], input[name="lastname"], input[data-param="last_name"]');
      if (!firstName) {
        const full = val('#ltk_name, input[name="name"], input[data-param="name"]');
        if (full) {
          const parts = full.split(/\s+/);
          firstName = parts.shift();
          if (!lastName) lastName = parts.join(' ');
        }
      }
      return { firstName, lastName };
    }

    setBusy(busy) {
      this.busy = busy;
      if (this.submitBtn) {
        this.submitBtn.disabled = busy;
        this.submitBtn.classList.toggle('is-loading', busy);
      }
    }

    // First required control left empty: inputs / selects with no value, or a
    // required radio / checkbox group with nothing checked.
    firstMissingRequired() {
      const controls = [...this.form.querySelectorAll('[required]')];
      const seenGroups = new Set();
      for (const el of controls) {
        if (el.disabled || el.type === 'hidden') continue;
        if (el.type === 'radio' || el.type === 'checkbox') {
          const key = el.name || el.id;
          if (seenGroups.has(key)) continue;
          seenGroups.add(key);
          const group = el.name ? this.form.querySelectorAll('input[name="' + CSS.escape(el.name) + '"]') : [el];
          if (![...group].some((g) => g.checked)) return el;
          continue;
        }
        if (!String(el.value || '').trim()) return el;
      }
      return null;
    }

    requiredMessage(el) {
      if (el.type === 'email') return 'Please enter a valid email address';
      if (el.type === 'tel') return 'Please enter your phone number';
      if (el.type === 'radio' || el.type === 'checkbox') return 'Please select an option';
      const label = (el.getAttribute('placeholder') || el.getAttribute('aria-label') || '').replace(/\s*\(optional\)/i, '').trim();
      return label ? 'Please enter your ' + label.toLowerCase() : 'Please fill in all required fields';
    }

    showError(message) {
      if (this.error) {
        this.error.textContent = message || '';
        this.error.hidden = !message;
      }
      if (!this.toast) return;
      clearTimeout(this.toastTimer);
      if (!message) {
        this.toast.hidden = true;
        return;
      }
      const text = this.toast.querySelector('.lpf__toast-text');
      if (text) text.textContent = message;
      // Re-trigger the entrance animation on repeated errors
      this.toast.hidden = true;
      void this.toast.offsetWidth;
      this.toast.hidden = false;
      this.toastTimer = setTimeout(() => {
        this.toast.hidden = true;
      }, 4000);
    }

    showSuccess() {
      this.showError('');
      // Fields stay visible (read-only); the submit button area is replaced
      // by the success message.
      const actions = this.form.querySelector('.lpf__actions');
      if (actions) actions.hidden = true;
      this.form.querySelectorAll('input, select').forEach((el) => {
        if (el.type !== 'hidden') el.readOnly = true;
      });
      if (this.success) {
        this.success.hidden = false;
        this.success.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
      this.dispatchEvent(new CustomEvent('lpf:success', { bubbles: true }));
    }
  }
  customElements.define('landing-page-form', LandingPageForm);
}
